import { useEffect, useState } from 'react';

import isEqual from 'react-fast-compare';

import { IOffer } from '@rbi-ctg/menu';
import { IOfferFeedbackEntryFragment } from 'generated/rbi-graphql';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { UIPattern } from 'state/offers/types';

export interface IUseCartPromoCodeOfferProps {
  sortedOffers: IOffer[];
  offersFeedback: Record<string, IOfferFeedbackEntryFragment> | undefined;
}

/**
 * @name useCartPromoCodeOffers
 * @description
 *   cart promo code offers are not displayed on the offers page and are separate from
 *   promo code offers (ui pattern: PROMO) because they are not displayed on the offers
 *   page and have a completely separate UX.
 */
export default function useCartPromoCodeOffers({
  sortedOffers = [],
  offersFeedback = {}
}: IUseCartPromoCodeOfferProps) {
  const enableCartPromoCodeOffers = useFlag(LaunchDarklyFlag.ENABLE_PROMO_CODE_AT_CHECKOUT);
  const [cartPromoCodeOffers, setCartPromoCodeOffers] = useState<IOffer[]>([]);

  // update cart promo code offers when sortedOffers changes
  useEffect(() => {
    if (enableCartPromoCodeOffers) {
      // only if cart promo code offers are enabled via LD
      // get all cart promo code offers and their unlocked status for this user
      const offers = sortedOffers.reduce((acc, offer: IOffer) => {
        if (offer.uiPattern === UIPattern.CART_PROMO) {
          const isRedeemable = !!offersFeedback[offer._id]?.redemptionEligibility?.isRedeemable;
          // ensure isRedeemable is properly set based on offersFeedback
          acc.push({ ...offer, isRedeemable });
        }
        return acc;
      }, [] as IOffer[]);
      setCartPromoCodeOffers(previousoffers =>
        isEqual(previousoffers, offers) ? previousoffers : offers
      );
    }
  }, [enableCartPromoCodeOffers, offersFeedback, sortedOffers]);

  // TODO: add function to update redeemable offers which can be
  //       used to display offers the user has already unlocked

  return {
    cartPromoCodeOffers
  };
}
